import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { AffiliateApplicationFormConfig } from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformAffiliateApplicationFormConfig } from '../infra/mappers';

interface UseAffiliateApplicationFormConfigParams {
  projectId: string;
  enabled?: boolean;
}

interface UseAffiliateApplicationFormConfigResult {
  formConfig: AffiliateApplicationFormConfig | null;
  isLoading: boolean;
  isError: boolean;
  isNotFound: boolean;
  error: Error | null;
  refetch: () => void;
  isRefetching: boolean;
}

export const useAffiliateApplicationFormConfig = ({
  projectId,
  enabled = true,
}: UseAffiliateApplicationFormConfigParams): UseAffiliateApplicationFormConfigResult => {
  const {
    data: response,
    isError,
    isLoading,
    error,
    refetch,
    isRefetching,
  }: UseQueryResult<AffiliateApplicationFormConfig, Error> = useQuery({
    queryKey: ['affiliate-application-form-config', projectId],
    queryFn: async () => {
      const data = await affiliateApplicationService.getFormConfig(projectId);
      return transformAffiliateApplicationFormConfig(data);
    },
    enabled: !!projectId && enabled,
  });

  const formConfig = useMemo(() => response ?? null, [response]);

  const isNotFound = useMemo(() => {
    // error.statusCode is set by mapToErrors; widening to access the non-standard property
    return (
      isError &&
      error instanceof Error &&
      'statusCode' in error &&
      (error as Error & { statusCode: number }).statusCode === 404
    );
  }, [isError, error]);

  return {
    formConfig,
    isLoading,
    isError,
    isNotFound,
    error,
    refetch,
    isRefetching,
  };
};
